// The lobby: a queue of rooms waiting to be joined.
//
// This is where the app opens, because the first question of a shift is "is
// there anything for me", not "what was the call ID again". A room here may
// have been created by another app entirely - dispatch defining a call id for a
// job - or by an inspector who started one and has not finished it.
//
// Joining does not happen here. Picking a room hands its id to the call view
// and puts you there with Join ready to press, because the moment before
// joining is when someone checks their camera, their microphone, and whether
// they are in a room with a door that shuts.

import { useCallback, useEffect, useState } from 'react';
import { InspectionsStore, type Inspection } from '../inspections/InspectionsStore';
import { guestLink, newCallId } from '../routing';
import { ORG_LABELS } from '../pricing/scope';
import { formatPhone } from '../inspections/phone';

/** Fresh enough that a room appears within a shift's attention span. */
const REFRESH_MS = 20_000;

function waitingFor(iso: string | null): string {
  if (!iso) return '';
  const since = new Date(iso).getTime();
  if (Number.isNaN(since)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - since) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return `${hours}h`;
}

export function LobbyView({
  onJoin,
}: {
  onJoin: (callId: string, orgId: string | null) => void;
}) {
  const [rooms, setRooms] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * The companies this inspector covers, and which one they are looking at.
   *
   * The endpoint has already filtered the rooms; this is only about reading
   * them. Somebody covering one company never sees the filter at all - there
   * is nothing to choose between.
   */
  const [covered, setCovered] = useState<string[]>([]);
  const [org, setOrg] = useState<string>('');

  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('');
  const [phone, setPhone] = useState('');
  const [newOrg, setNewOrg] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  /** Room id currently being texted, so the button can say so and not double-fire. */
  const [inviting, setInviting] = useState<string | null>(null);
  const [invited, setInvited] = useState<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const { data, problem: failed } = await InspectionsStore.open();
    if (data) {
      setRooms(data.inspections);
      setCovered(data.coveredOrgs ?? []);
    }
    setProblem(failed);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
    // Polled rather than pushed. A room appearing within twenty seconds is
    // soon enough for someone deciding what to do next, and it costs one
    // request against an endpoint that is already there - a socket would be a
    // lot of machinery for a list that changes a few times an hour.
    const timer = setInterval(() => void load(true), REFRESH_MS);
    const onFocus = () => void load(true);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const copyLink = useCallback(async (callId: string) => {
    try {
      await navigator.clipboard?.writeText(guestLink(callId));
      setCopied(callId);
      setTimeout(() => setCopied((c) => (c === callId ? null : c)), 2500);
    } catch {
      // Clipboard access can be refused. Not worth an error - the link is
      // reachable from the call itself too.
      setCopied(null);
    }
  }, []);

  /**
   * Makes the room and leaves it waiting, rather than dropping you into it.
   *
   * That is the point of a room: somewhere to send a customer before anyone is
   * on camera. Joining immediately is what the Call tab already does.
   */
  const create = useCallback(async () => {
    setSaving(true);
    const callId = newCallId();
    // Covering exactly one company needs no asking - there is only one lobby
    // this could belong to.
    const org = covered.length === 1 ? covered[0]! : newOrg;
    if (!org) {
      // Belt and braces behind the disabled button: a room with no company is
      // created and then invisible, which reads as the button being broken.
      setProblem('Choose which company this room is for.');
      setSaving(false);
      return;
    }
    const { problem: failed } = await InspectionsStore.createRoom(callId, label, phone, org);
    setSaving(false);
    if (failed) {
      setProblem(failed);
      return;
    }
    setLabel('');
    setPhone('');
    setNewOrg('');
    setCreating(false);
    // Straight onto the clipboard: a room exists to be sent to somebody, and
    // making that the next click rather than a hunt is most of its value.
    void copyLink(callId);
    await load(true);
  }, [label, phone, newOrg, covered, load, copyLink]);

  // Position in the queue, counted over waiting rooms only. A room somebody is
  // already on is not waiting for anyone, so numbering it would make the queue
  // longer than it is and put a number on a job nobody can take.
  // Filtered here rather than by refetching: the rooms are already the ones
  // this inspector covers, and a company with nothing waiting should still be
  // pickable so its empty queue is a fact rather than a missing option.
  const shown = org ? rooms.filter((room) => room.orgId === org) : rooms;

  // Counted over what is shown. A position is "where in this queue", and a
  // queue somebody has filtered to one company is that company's queue.
  let place = 0;
  const places = new Map<string, number>();
  for (const room of shown) {
    if (room.waiting) places.set(room.id, ++place);
  }

  const waitingIn = (id: string) =>
    rooms.filter((room) => room.orgId === id && room.waiting).length;

  const invite = useCallback(
    async (room: Inspection) => {
      setInviting(room.id);
      const { problem: failed } = await InspectionsStore.invite(room.id);
      setInviting(null);
      if (failed) {
        // Shown at the top rather than on the row: these are usually
        // configuration problems - an unset line, a missing company - and the
        // sentence is longer than a row has space for.
        setProblem(failed);
        return;
      }
      setProblem(null);
      setInvited(room.id);
      setTimeout(() => setInvited((r) => (r === room.id ? null : r)), 3000);
      await load(true);
    },
    [load],
  );

  return (
    <div className="lobby">
      <div className="lobby__head">
        <h2>Active rooms</h2>
        <span className="lobby__count">{rooms.length}</span>
        <div className="spacer" />
        {/* Nothing to create into. Covering no company means every one of them
            is somebody else's, so a room made here would be seen by nobody -
            including whoever made it. Better to say that than to accept the
            click and lose the room. */}
        <button
          type="button"
          className="play"
          disabled={loading || covered.length === 0}
          title={
            covered.length === 0 && !loading
              ? 'You are not assigned to a company, so a room would appear in no lobby.'
              : undefined
          }
          onClick={() => setCreating((v) => !v)}
          aria-expanded={creating}
        >
          {creating ? 'Cancel' : 'Create room'}
        </button>
        <button type="button" className="ghostbtn" disabled={loading} onClick={() => void load()}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {creating && (
        <form
          className="newroom"
          onSubmit={(e) => {
            e.preventDefault();
            if (!saving) void create();
          }}
        >
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="123 Oak St — burst pipe"
            aria-label="What this call is about"
          />
          <input
            className="newroom__phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            aria-label="Customer phone number"
          />
          {/* Asked for only when there is a choice. The company decides which
              lobby the room lands in, so a room without one is created and then
              seen by nobody. */}
          {covered.length > 1 && (
            <select
              value={newOrg}
              onChange={(e) => setNewOrg(e.target.value)}
              aria-label="Company this room is for"
              required
            >
              <option value="">Company&hellip;</option>
              {covered.map((id) => (
                <option key={id} value={id}>
                  {ORG_LABELS[id] ?? id}
                </option>
              ))}
            </select>
          )}
          <button
            type="submit"
            className="play"
            disabled={saving || (covered.length > 1 && !newOrg)}
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
          <span className="newroom__hint">
            The label and number are optional &mdash; they are how you tell one room from another
            in the queue, and the customer sees neither. The guest link is copied when the room is
            made.
            {covered.length > 1 && ' The company decides which lobby it appears in.'}
          </span>
        </form>
      )}

      {problem && (
        <p className="notice notice--bad">
          {problem}{' '}
          <button type="button" className="ghostbtn" onClick={() => void load()}>
            Try again
          </button>
        </p>
      )}

      {/* Only when there is a choice to make. One company needs no filter, and
          a row of one button would just be furniture. */}
      {covered.length > 1 && (
        <div className="lobbyorgs" role="group" aria-label="Company">
          <button
            type="button"
            className={`lobbyorg${org === '' ? ' lobbyorg--on' : ''}`}
            onClick={() => setOrg('')}
          >
            All
            <span className="lobbyorg__count">{rooms.filter((r) => r.waiting).length}</span>
          </button>
          {covered.map((id) => (
            <button
              key={id}
              type="button"
              className={`lobbyorg${org === id ? ' lobbyorg--on' : ''}`}
              onClick={() => setOrg(id)}
            >
              {ORG_LABELS[id] ?? id}
              <span className="lobbyorg__count">{waitingIn(id)}</span>
            </button>
          ))}
        </div>
      )}

      {!loading && !problem && shown.length === 0 && rooms.length > 0 && (
        <p className="empty-note">
          Nothing waiting for {ORG_LABELS[org] ?? org}. Other companies have rooms &mdash; choose
          All to see them.
        </p>
      )}

      {!loading && !problem && rooms.length === 0 && (
        <p className="empty-note">
          Nothing in the queue. Rooms appear here when another app creates a call, or when one is
          created above &mdash; oldest first, so the top of the list is always next.
        </p>
      )}

      <ul className="rooms">
        {shown.map((room) => {
          const position = places.get(room.id);
          const next = position === 1;
          return (
          <li
            key={room.id}
            className={`room${room.waiting ? '' : ' room--live'}${next ? ' room--next' : ''}`}
          >
            <button
              type="button"
              className="room__body"
              onClick={() => onJoin(room.callId, room.orgId)}
            >
              {/* The number is the whole point of a queue: what is next, and
                  how much is behind it. In-progress rooms get a dash rather
                  than a number - they are not waiting for anybody. */}
              <span className="room__place">{position ?? '—'}</span>
              <span className="room__label">
                {room.label || formatPhone(room.phoneNumber) || 'Untitled call'}
                {/* The company, when the room carries one. Shown because it
                    decides the price, and an estimator picking between rooms
                    should not have to open one to find out whose it is. */}
                {room.orgId && <span className="room__org">{room.orgId}</span>}
                {next && <span className="room__next">Next up</span>}
              </span>
              <span className="room__meta">
                {room.waiting ? (
                  <>
                    Waiting {waitingFor(room.createdAt)}
                    {room.createdByApp ? ` · from ${room.createdByApp}` : ''}
                  </>
                ) : (
                  <>
                    In progress
                    {room.acceptedBy ? ` · ${room.acceptedBy}` : ''}
                  </>
                )}
              </span>
              {/* Its own column rather than a fallback for a missing label.
                  Whose number it is, is not the same fact as what the job is,
                  and an estimator glancing down the queue wants both. */}
              <span className="room__phone">
                {room.phoneNumber ? formatPhone(room.phoneNumber) : ''}
              </span>
              <span className="room__id" title={room.callId}>
                {room.callId.slice(0, 8)}
              </span>
              <span className="room__go">{room.waiting ? 'Take it' : 'Join'}</span>
            </button>
            {/* Outside the row's button, because copying a link and walking
                into the room are different intentions and nesting one inside
                the other would make the whole row ambiguous. */}
            {/* Only where there is somebody to text. A button that explains
                it cannot work is worse than one that is not there. */}
            {room.phoneNumber && (
              <button
                type="button"
                className="room__invite"
                disabled={inviting === room.id}
                onClick={() => void invite(room)}
                title={
                  room.invitedAt
                    ? `Already texted ${formatPhone(room.phoneNumber)} — send it again`
                    : `Text the link to ${formatPhone(room.phoneNumber)}`
                }
              >
                {inviting === room.id
                  ? 'Sending…'
                  : invited === room.id
                    ? 'Sent'
                    : room.invitedAt
                      ? 'Resend'
                      : 'Send invite'}
              </button>
            )}
            <button
              type="button"
              className="room__copy"
              onClick={() => void copyLink(room.callId)}
              title="Copy the link to send the customer"
            >
              {copied === room.callId ? 'Copied' : 'Copy link'}
            </button>
          </li>
          );
        })}
      </ul>
    </div>
  );
}
